import { getImageAttrs } from '~/modules/images';
import { useRevampPublishDate } from './RevampPublishDate';
import { REVAMP_PREMIUM_HERO_SIZES } from '../util/revamp-image-sizes';

export interface RevampPremiumTitleSectionProps {
    /** Full-bleed background — the article's existing TopBanner image. */
    bannerImageUrl: string;
    /** Alt text for the banner. This section renders no visible caption, so it
     *  is the only description a screen reader gets. */
    alt: string;
    /** Article headline, set in the large Gestura display face. */
    title: string;
    /** "Featuring Projects by" credit line(s). */
    featuring?: string;
    /** Author name for the "Written by" credit. */
    writtenBy?: string;
    /** Raw publish date from the loader; formatted here, not by the caller. */
    publishDate?: string;
    /** Editorial pull-quote (the BM "subheading"); rendered at the foot. */
    subheading?: string;
}

/**
 * Premium editorial title section (Figma 2479:73336) — a full-bleed banner
 * photo with the credits row, display headline and an editorial pull-quote
 * overlaid in cream/white. Replaces the standard header + teaser + hero stack
 * for premium articles. Sits below the global header like the other detail
 * heroes (the banner does not run behind the nav). Sizes in rem at the 1920
 * artboard (÷16), base + md: only, matching the article-revamp siblings.
 */
export const RevampPremiumTitleSection = ({
    bannerImageUrl,
    alt,
    title,
    featuring,
    writtenBy,
    publishDate,
    subheading,
}: RevampPremiumTitleSectionProps) => {
    // Localised here for the same hydration reason as the other bylines — see
    // useRevampPublishDate.
    const date = useRevampPublishDate(publishDate);
    const writtenByLine = [writtenBy, date?.label].filter(Boolean).join(', ');

    return (
        <section className="bg-neutral-900 relative aspect-[375/720] w-full overflow-hidden text-white md:aspect-[1920/2354]">
            <img
                {...getImageAttrs(bannerImageUrl, {
                    sizes: REVAMP_PREMIUM_HERO_SIZES,
                })}
                alt={alt}
                fetchPriority="high"
                decoding="async"
                className="absolute inset-0 size-full object-cover"
            />
            {/* Legibility scrims — top backs the credits/headline, bottom backs
                the pull-quote; the banner photo is already warm and dark. */}
            <div className="absolute inset-x-0 top-0 h-[38%] bg-linear-to-b from-black/55 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-[34%] bg-linear-to-t from-black/60 to-transparent" />

            {/* Above qhd the header stops growing — it caps at 1920 and
                centres — so the content box is capped the same way to keep the
                credits and headline on the nav links. The photo behind stays
                full-bleed. */}
            <div className="relative flex h-full flex-col justify-between px-4.5 pt-28 pb-8 qhd:mx-auto qhd:max-w-[1920px] md:px-12 md:pt-[29.375rem] md:pb-[10.4rem]">
                {/* Top: divider, credits row and the display headline. */}
                <div>
                    <hr className="m-0 border-t border-white" />
                    {/* The first column is sized so the credits and headline
                        start on the header's nav links rather than the design's
                        own 25.3%. That offset is logo + gaps inside the header,
                        not a round fraction, so it is matched empirically. The
                        exact figure drifts a little with width (the offset is
                        part fixed px, part fluid); 26.4% is the compromise that
                        stays within ~3px from 1440 up through 2560. Re-measure
                        if the header's logo or gaps change. */}
                    <div className="mt-[1.125rem] flex flex-col gap-3 font-sans text-[0.8125rem] font-bold tracking-[-0.03em] md:grid md:grid-cols-[26.4%_42.3%_1fr] md:gap-0 md:text-[1rem] md:leading-[1.1875rem]">
                        <p className="m-0">(Premium Editorial)</p>
                        <p className="m-0 md:max-w-[17.25rem]">
                            {featuring && (
                                <>
                                    Featuring Projects by
                                    <br />
                                    {featuring}
                                </>
                            )}
                        </p>
                        <p className="m-0 md:max-w-[17.25rem]">
                            {writtenByLine && (
                                <>
                                    Written by
                                    <br />
                                    {writtenByLine}
                                </>
                            )}
                        </p>
                    </div>
                    {/* Shares the credits' second-column edge, so this tracks
                        the grid above it. */}
                    <h1 className="m-0 mt-6 font-gestura text-[2.5rem] leading-[0.98] font-normal tracking-[-0.03em] text-[#fffcf5] md:mt-[5.8rem] md:ml-[26.4%] md:max-w-[68.5rem] md:text-[7.5rem] md:leading-[0.94] md:tracking-[-0.3rem]">
                        {title}
                    </h1>
                </div>

                {/* Foot: editorial pull-quote (the BM subheading). */}
                {subheading && (
                    <figure className="relative m-0 mt-10 md:ml-[9.3rem]">
                        <span
                            aria-hidden="true"
                            className="font-sans text-[1.25rem] md:absolute md:top-[1.4rem] md:left-0 md:text-[1.75rem] md:tracking-[-0.044em]"
                        >
                            (→)
                        </span>
                        <p className="m-0 mt-2 font-gestura text-[1.5rem] leading-[1.07] font-normal md:mt-0 md:max-w-[94.9rem] md:indent-[18.4rem] md:text-[5rem] md:tracking-[-0.24rem]">
                            {subheading}
                        </p>
                    </figure>
                )}
            </div>
        </section>
    );
};
